import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import { type Banner, type Page as SanityPage, getPage, getAnnouncementBanner } from "lib/sanity";
import { HalfHero } from "components/half-hero";
import { Page } from "components/page";
import { Customers } from "components/logos";
import { DemoEmbedHalf } from "components/demo-embed";
import SecurityPairs from "components/platform/security/security-pairs";
import { Text } from "@chakra-ui/react";
import { getPageMetaImage } from "utils";
import { SecurityFeatures } from "components/content";
import heroImage from "../../../public/assets/solutions/healthcare/hero.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
};

const Healthcare: NextPage<Readonly<PageProps>> = ({ page, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      {page.customers && <Customers customers={page.customers} />}

      <SecurityFeatures />

      <DemoEmbedHalf
        title="Activate PHI data securely"
        description={
          <Text>
            Safely sync PHI data from your warehouse to HIPAA Compliant destinations. Hightouch automatically hashes PII
            when syncing to platforms, plus we sync de-identified data to tools where you don’t have a BAA.
          </Text>
        }
      />

      <SecurityPairs />
    </Page>
  );
};

export default Healthcare;
export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getPage("/solutions/healthcare"), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};
